'use client';

import React from 'react';

import { Checkbox } from '@depop/web-ui-kit/Checkbox';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { Dropdown } from '@/components/Dropdown';
import { ColourCircle } from '@/components/Filters/ColourFilter/ColourCircle';
import { MobileFilterFooter } from '@/components/Filters/MobileFilterFooter';
import { MobileFilterHeader } from '@/components/Filters/MobileFilterHeader';
import { SearchFilterTrigger } from '@/components/Filters/SearchFilterTrigger';
import baseFilterStyles from '@/components/Filters/baseFilterStyles.module.css';
import { useProductAttributes } from '@/modules/attributes/useProductAttributes';
import { toggleInArray } from '@/utils/toggleItemInArray';

type YMALColourFilterProps = {
  colours?: string[];
  colourOptions: string[];
  onColourFilterChange: (
    coloursOrUpdater: string[] | ((prev: string[] | undefined) => string[])
  ) => void;
  onClearFilters: () => void;
  closeMobileFilterDrawer: () => void;
  isFilterableYMALExperiment?: boolean;
};

export function YMALColourFilter({
  colours,
  colourOptions,
  onColourFilterChange,
  onClearFilters,
  closeMobileFilterDrawer,
  isFilterableYMALExperiment,
}: YMALColourFilterProps) {
  const t = useTranslations('common');
  const title = t('Colour.Title');

  // Get color data with proper names and hex codes
  const { data: attributesData } = useProductAttributes();
  const coloursData = attributesData?.colour;

  if (colourOptions.length === 0) {
    return null;
  }

  // Filter to only show available colors
  const availableColours = coloursData?.filter((colour) =>
    colourOptions.includes(colour.id)
  );

  return (
    <Dropdown
      TriggerComponent={(props) => (
        <SearchFilterTrigger
          {...props}
          isActive={Boolean(colours?.length)}
          title={title}
        />
      )}
    >
      {({ className, toggleDropdown }) => (
        <div
          className={clsx(
            className,
            baseFilterStyles.searchFilterDropdownContents,
            styles.colourFilterDropdownContents
          )}
        >
          <MobileFilterHeader
            title={title}
            toggleDropdown={toggleDropdown}
            closeMobileFilterDrawer={closeMobileFilterDrawer}
          />
          <div className={baseFilterStyles.content}>
            {availableColours?.map((colour) => {
              const isChecked = colours?.includes(colour.id);
              const checkboxId = `ymal-checkbox-${colour.id}`;

              return (
                <label
                  className={clsx(
                    baseFilterStyles.checkboxFilterDropdownOption,
                    styles.colourFilterDropdownOptionContainer,
                    styles.colourFilterDropdownOptionLabel
                  )}
                  style={
                    {
                      '--font-weight': isChecked ? 'bold' : 'normal',
                    } as React.CSSProperties
                  }
                  key={colour.id}
                  htmlFor={checkboxId}
                >
                  <ColourCircle colour={colour} />
                  <Checkbox
                    id={checkboxId}
                    checked={isChecked}
                    onChange={() =>
                      onColourFilterChange((prev) =>
                        toggleInArray(prev, colour.id)
                      )
                    }
                  />
                </label>
              );
            })}
          </div>
          <MobileFilterFooter
            isFilterableYMALExperiment={isFilterableYMALExperiment}
            handleClear={onClearFilters}
            handleSubmit={closeMobileFilterDrawer}
            hideResultsCount={true}
          />
        </div>
      )}
    </Dropdown>
  );
}
